import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { CreditService } from '../credit-service';
import { type ApiPageInitResponse } from '@/infrastructure/api-functions/core-drafting-api/page-init-api';
import { CreditModel } from '../../models/credit-model';
import { Logger } from '@patsnap/tahiti-core';
import { SharePageService } from '@/services/share-page/share-page-service';
import type { CreditUsageKey } from '../../types';

type EndsWith<Str extends string, Suffix extends string> = Str extends `${any}${Suffix}` ? Str : never;

type GetUsageKeys<T> = {
    [K in keyof T]: K extends string ? EndsWith<K, '_usage'> : never;
}[keyof T];

// 使用示例
type UsageKeys = GetUsageKeys<ApiPageInitResponse>;

@injectable()
export class CreditServiceImpl implements CreditService {
    constructor(
        @inject(CreditModel)
        private readonly _creditModel: CreditModel,
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(SharePageService)
        private readonly _sharePageService: SharePageService,
    ) {}

    async syncCreditUsage(): Promise<void> {
        const response = await this._sharePageService.pageInitProxy();
        const remainingNoveltySearchTimes = this._getRemainingTimes(response, 'novelty_search_usage');
        const remainingFtoSearchTimes = this._getRemainingTimes(response, 'fto_search_usage');
        const remainingPatentDisclosureTimes = this._getRemainingTimes(response, 'patent_disclosure_usage');
        const remainingTranslationTimes = this._getRemainingTimes(response, 'translation_usage');
        const remainingCnPatentSpecificationTimes = this._getRemainingTimes(response, 'cn_patent_specification_usage');
        const remainingUsPatentSpecificationTimes = this._getRemainingTimes(response, 'us_patent_specification_usage');
        const remainingEuPatentSpecificationTimes = this._getRemainingTimes(response, 'eu_patent_specification_usage');
        const usages = this._getUsages(response);

        this._creditModel.update(() => {
            return {
                remainingNoveltySearchTimes,
                remainingFtoSearchTimes,
                remainingPatentDisclosureTimes,
                remainingTranslationTimes,
                remainingCnPatentSpecificationTimes,
                remainingUsPatentSpecificationTimes,
                remainingEuPatentSpecificationTimes,
                usages,
            };
        });
    }

    private _getRemainingTimes(response: ApiPageInitResponse, usageKey: UsageKeys) {
        try {
            return response[usageKey].total - response[usageKey].usage;
        } catch (error) {
            this._logger.warn(`获取剩余${usageKey}次数失败`, error);
            return 0;
        }
    }

    // eslint-disable-next-line complexity, max-lines-per-function
    private _getUsages(response: ApiPageInitResponse): Map<CreditUsageKey, {
        usage: number;
        total: number;
    }> {
        const usages = new Map<CreditUsageKey, {
            usage: number;
            total: number;
        }>();

        usages.set('novelty_search_usage', {
            usage: response.novelty_search_usage?.usage ?? 0,
            total: response.novelty_search_usage?.total ?? 0,
        });

        usages.set('fto_search', {
            usage: response.fto_search_usage?.usage ?? 0,
            total: response.fto_search_usage?.total ?? 0,
        });

        usages.set('specification', {
            usage: response.specification_usage?.usage ?? 0,
            total: response.specification_usage?.total ?? 0,
        });

        usages.set('patent_disclosure', {
            usage: response.patent_disclosure_usage?.usage ?? 0,
            total: response.patent_disclosure_usage?.total ?? 0,
        });

        usages.set('translation', {
            usage: response.translation_usage?.usage ?? 0,
            total: response.translation_usage?.total ?? 0,
        });

        usages.set('cn_patent_specification', {
            usage: response.cn_patent_specification_usage?.usage ?? 0,
            total: response.cn_patent_specification_usage?.total ?? 0,
        });

        usages.set('us_patent_specification', {
            usage: response.us_patent_specification_usage?.usage ?? 0,
            total: response.us_patent_specification_usage?.total ?? 0,
        });

        usages.set('eu_patent_specification', {
            usage: response.eu_patent_specification_usage?.usage ?? 0,
            total: response.eu_patent_specification_usage?.total ?? 0,
        });

        usages.set('unified_patent_specification_usage', {
            usage: response.unified_patent_specification_usage?.usage ?? 0,
            total: response.unified_patent_specification_usage?.total ?? 0,
        });

        usages.set('unified_usage', {
            usage: response.unified_usage?.usage ?? 0,
            total: response.unified_usage?.total ?? 0,
        });

        usages.set('unified_pro_usage', {
            usage: response.unified_pro_usage?.usage ?? 0,
            total: response.unified_pro_usage?.total ?? 0,
        });

        usages.set('unified_free_usage', {
            usage: response.unified_free_usage?.usage ?? 0,
            total: response.unified_free_usage?.total ?? 0,
        });

        return usages;
    }
}
